import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { dexisTokenLoginTemplate } from 'apollo/templates/scanner-case';

const DexisAuthLandingPage: FC = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [authState, setAuthState] = useState(AuthState.AUTH_IN_PROGRESS);
    const { t: translate } = useTranslation(['scan-cases']);
    const hasRun = useRef(false);

    const [dexisTokenLogin] = useMutation(dexisTokenLoginTemplate, {
        onCompleted: ({ dexisTokenLogin }) => {
            if (dexisTokenLogin) {
                setAuthState(AuthState.AUTH_SUCCESSFUL);
                setTimeout(() => {
                    navigate('/orders/scanner-cases');
                }, 3000);
                return;
            }
            setAuthState(AuthState.AUTH_FAILED);
        },
        onError: () => {
            setAuthState(AuthState.AUTH_FAILED);
        },
    });

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        const authCode = new URLSearchParams(search).get('code');
        if (authCode) {
            dexisTokenLogin({ variables: { authCode: authCode } });
            return;
        }
        setAuthState(AuthState.AUTH_FAILED);
    }, [search]);

    return (
        <Fragment>
            <Box display="flex" justifyContent="center" sx={{ minHeight: 300 }}>
                <Box sx={{ margin: 'auto', maxWidth: 600 }}>
                    {(() => {
                        switch (authState) {
                            case AuthState.AUTH_IN_PROGRESS:
                                return (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <CircularProgress variant="indeterminate" color="secondary" />
                                        <Typography variant="h3" mt={2}>
                                            {translate('scan-cases:PROCESSING_DEXIS_LOGIN')}
                                        </Typography>
                                    </Box>
                                );
                            case AuthState.AUTH_SUCCESSFUL:
                                return (
                                    <Alert severity="success">
                                        <AlertTitle>{translate('scan-cases:DEXIS_AUTH_SUCCESSFUL_TITLE')}</AlertTitle>
                                        <Box>{translate('scan-cases:SCAN_CASE_AUTH_SUCCESSFUL_BODY')}</Box>
                                    </Alert>
                                );
                            case AuthState.AUTH_FAILED:
                                return (
                                    <Alert severity="error">
                                        <AlertTitle>{translate('scan-cases:DEXIS_AUTH_ERROR_TITLE')}</AlertTitle>
                                        <Box>{translate('scan-cases:DEXIS_AUTH_ERROR_BODY')}</Box>
                                    </Alert>
                                );
                            default:
                                return null;
                        }
                    })()}
                </Box>
            </Box>
        </Fragment>
    );
};

enum AuthState {
    AUTH_IN_PROGRESS,
    AUTH_SUCCESSFUL,
    AUTH_FAILED,
}

export default DexisAuthLandingPage;
